import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { subscribeToUpdate, applyUpdate, isWorkoutRoute } from '../../utils/serviceWorkerUpdate';
import './UpdateBanner.css';

/**
 * Faixa «Nova versão do GyMag disponível» + «Atualizar» / «Depois».
 *
 *  - Aparece quando o service worker tem uma versão nova em espera (ver utils/serviceWorkerUpdate.js).
 *  - «Atualizar» pede ao worker que assuma o controlo e recarrega a página quando ele assume:
 *    a sessão (token no localStorage) mantém-se, não há logout.
 *  - «Depois» esconde e não insiste na MESMA versão; só volta a aparecer com uma versão ainda mais nova.
 *  - Nunca a meio de um treino (o recarregamento perdia as séries que estão no ecrã).
 *  - Sem service worker ativo não aparece nada: não há falsos avisos.
 */
export default function UpdateBanner({ onReload } = {}) {
  const location = useLocation();
  const [worker, setWorker] = useState(null);
  const [dismissedFor, setDismissedFor] = useState(null);
  const [applied, setApplied] = useState(false);

  const appliedRef = useRef(false);
  const reloadRef = useRef(onReload);
  const fallbackRef = useRef(null);
  reloadRef.current = onReload;

  const reload = useCallback(() => {
    if (fallbackRef.current) {
      clearTimeout(fallbackRef.current);
      fallbackRef.current = null;
    }
    if (reloadRef.current) reloadRef.current();
    else window.location.reload();
  }, []);

  useEffect(() => subscribeToUpdate(setWorker), []);

  useEffect(() => {
    const serviceWorker = typeof navigator !== 'undefined' ? navigator.serviceWorker : null;
    if (!serviceWorker || typeof serviceWorker.addEventListener !== 'function') return undefined;

    const onControllerChange = () => {
      // Só se recarrega quando o utilizador pediu a atualização: na primeira instalação o
      // `clients.claim()` também dispara este evento e um recarregamento aí não faz sentido.
      if (appliedRef.current) reload();
    };
    serviceWorker.addEventListener('controllerchange', onControllerChange);

    return () => {
      serviceWorker.removeEventListener('controllerchange', onControllerChange);
      if (fallbackRef.current) {
        clearTimeout(fallbackRef.current);
        fallbackRef.current = null;
      }
    };
  }, [reload]);

  const handleUpdate = () => {
    if (appliedRef.current) return;
    appliedRef.current = true;
    setApplied(true);

    if (!applyUpdate()) {
      reload();
      return;
    }
    // Se o browser não entregar `controllerchange`, não se fica à espera para sempre.
    fallbackRef.current = setTimeout(reload, 3000);
  };

  const visible = !!worker && worker !== dismissedFor && !isWorkoutRoute(location.pathname);
  if (!visible) return null;

  return (
    <div className="update-banner" role="status" aria-live="polite" data-testid="update-banner">
      <p className="update-banner__text">Nova versão do GyMag disponível</p>
      <div className="update-banner__actions">
        <button
          type="button"
          className="update-banner__btn"
          onClick={() => setDismissedFor(worker)}
          disabled={applied}
        >
          Depois
        </button>
        <button
          type="button"
          className="update-banner__btn update-banner__btn--primary"
          onClick={handleUpdate}
          disabled={applied}
        >
          {applied ? 'A atualizar…' : 'Atualizar'}
        </button>
      </div>
    </div>
  );
}
